import type { ReactNode } from 'react'
import { ChevronLeft, Home, PanelLeft, PanelLeftClose } from 'lucide-react'
import { RfidMenuButton } from './RfidMenuButton'
import { useRfidDrawerOptional } from '@/contexts/RfidDrawerContext'
import { cn } from '@/utils/cn'

export type RfidAppHeaderProps = {
  title: string
  subtitle?: string
  onBack?: () => void
  onHome?: () => void
  /** LEAP favicon menu (mobile). Default on when drawer provider is present. */
  showMenu?: boolean
  right?: ReactNode
  className?: string
}

export function RfidAppHeader({
  title,
  subtitle,
  onBack,
  onHome,
  showMenu,
  right,
  className,
}: RfidAppHeaderProps) {
  const drawer = useRfidDrawerOptional()
  const menuVisible = showMenu ?? Boolean(drawer)
  const collapsed = drawer?.sidebarCollapsed ?? false

  return (
    <header className={cn('rfid-app-header', className)}>
      {menuVisible ? (
        <span className="lg:hidden">
          <RfidMenuButton />
        </span>
      ) : null}
      {/* Desktop sidebar collapse — CONSUMPTION_RE_FE topbar parity */}
      {drawer ? (
        <button
          type="button"
          className="rfid-app-header__icon-btn hidden lg:inline-flex"
          onClick={drawer.toggleSidebarCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <PanelLeft size={20} strokeWidth={2.5} />
          ) : (
            <PanelLeftClose size={20} strokeWidth={2.5} />
          )}
        </button>
      ) : null}
      {onHome ? (
        <button
          type="button"
          className="rfid-app-header__icon-btn"
          onClick={onHome}
          aria-label="Go home"
        >
          <Home size={20} strokeWidth={2.5} />
        </button>
      ) : null}
      {onBack ? (
        <button
          type="button"
          className="rfid-app-header__icon-btn"
          onClick={onBack}
          aria-label="Go back"
        >
          <ChevronLeft size={22} strokeWidth={2.5} />
        </button>
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="rfid-app-header__title truncate">{title}</div>
        {subtitle ? (
          <div className="rfid-app-header__subtitle truncate">{subtitle}</div>
        ) : null}
      </div>
      {right ? <div className="max-w-[min(100%,280px)] shrink-0">{right}</div> : null}
    </header>
  )
}
